'use client';

import { type RefObject, useCallback, useEffect, useRef } from 'react';

/**
 * Manages "peek" cards in the ArticleGrid carousel — cards that partially overflow
 * the visible area to hint at more content. Disables pointer events on peek cards so
 * clicks fall through to a handler that advances the carousel. Observes Embla's style
 * mutations and window resizes to keep the peek state current.
 */
export function usePeekCards(): RefObject<HTMLDivElement | null> {
  const gridRef = useRef<HTMLDivElement>(null);

  const markPeekCards = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const contentEl = grid.closest('[data-testid="carousel-content"]');
    if (!contentEl) return;
    const visibleRight = contentEl.getBoundingClientRect().right;
    const cards = grid.querySelectorAll<HTMLElement>('[data-testid="smallArticle"]');
    cards.forEach(card => {
      const isPeek = card.getBoundingClientRect().right > visibleRight;
      card.style.pointerEvents = isPeek ? 'none' : '';
    });
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const contentEl = grid.closest('[data-testid="carousel-content"]');

    markPeekCards();

    const observer = new MutationObserver(markPeekCards);
    observer.observe(grid, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', markPeekCards);

    // Listener lives on contentEl (not the grid) because Embla translates the grid
    // off-screen as it scrolls — clicks in the peek zone would miss the grid.
    const handlePeekClick = (e: Event) => {
      if (!(e instanceof MouseEvent)) return;
      const gridRect = grid.getBoundingClientRect();
      if (e.clientY < gridRect.top || e.clientY > gridRect.bottom) return;
      const cards = grid.querySelectorAll<HTMLElement>('[data-testid="smallArticle"]');
      const clickedInPeekColumn = Array.from(cards).some(
        card =>
          card.style.pointerEvents === 'none' &&
          card.getBoundingClientRect().left <= e.clientX &&
          e.clientX <= card.getBoundingClientRect().right,
      );
      if (clickedInPeekColumn) {
        const nextBtn = contentEl?.querySelector<HTMLButtonElement>('button[aria-label="Next slide"]');
        nextBtn?.click();
      }
    };

    contentEl?.addEventListener('click', handlePeekClick);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', markPeekCards);
      contentEl?.removeEventListener('click', handlePeekClick);
    };
  }, [markPeekCards]);

  return gridRef;
}
